import {isAndroid, isWindows, Message, postMessageToAndroid, postMessageToWindows} from "./clientCommunication";

/**
 * Provides a simple way to get a Facebook access token. This class uses different methods to get an access token
 * depending on the platform and then returns it via the provided onAccessToken callback.
 */
export class FacebookAccessTokenGetter {
  constructor(onAccessToken) {
    this.onAccessToken = onAccessToken
  }

  get() {
    if (isAndroid(window.queryParams.device)) {
      // Facebook doesn't allow login from Android WebView, so we have to ask the Android app to get an access token
      // via the Facebook Android SDK
      this.getAccessTokenAndroid()
    } else if (isWindows(window.queryParams.device)) {
      // Facebook login doesn't work from embedded browser on Windows, so we ask the Windows app to get an access token
      // via a dvo.com webpage loaded in an external browser.
      this.getAccessTokenWindows()
    } else {
      // For every other platform, use Facebook JavaScript SDK
      this.getAccessTokenJSSDK()
    }
  }


  // JavaScript SDK

  getAccessTokenJSSDK() {
    window.FB.getLoginStatus(response => {
      let accessToken = null
      const isLoggedIn = response.status === 'connected'
      // console.log("getAccessTokenJSSDK: response=[" + JSON.stringify(response) + "]")
      console.log("getAccessTokenJSSDK: isLoggedIn=[" + isLoggedIn + "]")
      if (isLoggedIn) {
        accessToken = response.authResponse.accessToken
        // console.log("getAccessTokenJSSDK: accessToken=[" + accessToken + "]")
      }
      // Give access token back to the receiver
      this.onAccessToken(accessToken)
    })
  }


  // Android

  getAccessTokenAndroid() {
    console.log("getAccessTokenAndroid")
    // Enable the Android app to call back
    window.facebookAccessTokenGetter = this

    // Tell the Android app to get an access token
    postMessageToAndroid(new Message(Message.ACTION_GET_FACEBOOK_ACCESS_TOKEN, {}))
  }

  androidDidSendAccessToken(accessToken) {
    console.log("androidDidSendAccessToken: accessToken=[" + accessToken + "]")

    // The Android app can only send string values, convert "null" and "" to JavaScript null
    if (accessToken === undefined || accessToken === "null" || accessToken === "") {
      accessToken = null;
    }

    // Android responded with an access token, send it back to the receiver
    this.onAccessToken(accessToken)
  }


  // Windows

  getAccessTokenWindows() {
    console.log("getAccessTokenWindows")
    // Enable the Windows app to call back
    window.facebookAccessTokenGetter = this

    // Tell the Windows app to get an access token
    postMessageToWindows(new Message(Message.ACTION_GET_FACEBOOK_ACCESS_TOKEN, {}))
  }

  windowsDidSendAccessToken(accessToken) {
    console.log("windowsDidSendAccessToken: accessToken=[" + accessToken + "]")

    // The Windows app can only send string values, convert "null" and "" to JavaScript null
    if (accessToken === undefined || accessToken === "null" || accessToken === "") {
      accessToken = null;
    }

    // Send accessToken back to the receiver
    this.onAccessToken(accessToken)
  }
}


/**
 * Provides an easy way to request specific Facebook permissions. This class uses different methods of requesting
 * Facebook permissions depending on the current platform, then responds to the onResponse callback with a boolean
 * indicating whether or not the requested permissions where granted.
 */
export class FacebookPermissionGetter {
  constructor(onResponse, accessToken, permissions) {
    this.onResponse = onResponse
    this.accessToken = accessToken
    this.permissions = permissions
  }

  get() {
    if (isAndroid(window.queryParams.device)) {
      // Facebook doesn't allow login from Android WebView, so we have to ask the Android app to login
      this.getAndroid()
    } else if (isWindows(window.queryParams.device)) {
      // Facebook login doesn't work in Windows embedded browser, so we have to ask the Windows app to get permissions
      this.getWindows()
    } else {
      // For every other platform, use Facebook JavaScript SDK
      this.getJSSDK()
    }
  }


  // JavaScript SDK

  getJSSDK() {
    let scope = ''
    for (let i = 0; i < this.permissions.length; i++) {
      const permission = this.permissions[i]
      scope = scope + permission
      if (i < this.permissions.length - 1) {
        scope = scope + ","
      }
    }
    console.log("getJSSDK: scope=[" + scope + "]")
    window.FB.login(response => {
      if (response.status === "unknown") {
        this.onResponse(false)
        return
      }

      // Verify if permissions are actually granted
      arePermissionsGranted((granted) => {
        this.onResponse(granted)
      }, this.accessToken, this.permissions)
    }, { scope: scope, auth_type: 'rerequest', access_token: this.accessToken })
  }


  // Android

  getAndroid() {
    console.log("getAndroid")
    // Enable the Android app to call back
    window.facebookPermissionGetter = this

    // Tell the Android app to get an access token
    postMessageToAndroid(new Message(Message.ACTION_GET_FACEBOOK_PERMISSIONS, { permissions: this.permissions }))
  }

  androidDidSendResponse(isGranted) {
    // Android app can only send string values, so isGranted with either be "t" or "f"
    console.log("androidDidSendResponse: isGranted=[" + isGranted + "]")

    const isGrantedBool = isGranted === "t";
    this.onResponse(isGrantedBool)
  }


  // Windows

  getWindows() {
    console.log("getWindows")
    // Enable the Windows app to call back
    window.facebookPermissionGetter = this

    // Tell the Windows app to get an access token
    postMessageToWindows(new Message(Message.ACTION_GET_FACEBOOK_PERMISSIONS, { permissions: this.permissions }))
  }

  windowsDidSendResponse(isGranted) {
    // Windows app can only send string values, so isGranted with either be "t" or "f"
    console.log("windowsDidSendResponse: isGranted=[" + isGranted + "]")

    const isGrantedBool = isGranted === "t";
    this.onResponse(isGrantedBool)
  }
}


/**
 * Provides an easy way to login with Facebook. This class uses different methods to login with Facebook depending on
 * the current platform, then calls the onResponse callback with the access token of the logged in user. If the user
 * failed to login, then null is passed back.
 */
export class FacebookLogin {
  constructor(onResponse) {
    this.onResponse = onResponse
  }

  login() {
    if (isAndroid(window.queryParams.device)) {
      // Facebook doesn't allow login from Android WebView, so we have to ask the Android app to login
      this.loginAndroid()
    } else if (isWindows(window.queryParams.device)) {
      // Facebook login doesn't work from Windows embedded browser, so we have to ask the Windows app to login
      this.loginWindows()
    } else {
      // For every other platform, use Facebook JavaScript SDK
      this.loginJSSDK()
    }
  }


  // JavaScript SDK

  loginJSSDK() {
    window.FB.login(response => {
      if (response.authResponse) {
        // User successfully logged in with Facebook
        console.log("loginJSSDK: Successfully logged in")

        // Send accessToken back to caller
        const accessToken = response.authResponse.accessToken
        this.onResponse(accessToken)
      } else {
        // User failed to login with Facebook
        console.log("loginJSSDK: Failed to log in")
        this.onResponse(null)
      }
    }, { scope: "user_friends" })
  }


  // Android

  loginAndroid() {
    console.log("loginAndroid")
    // Enable the Android app to call back
    window.facebookLogin = this

    // Tell the Android app to get an access token
    postMessageToAndroid(new Message(Message.ACTION_DO_FACEBOOK_LOGIN, {}))
  }

  androidDidLogin(accessToken) {
    console.log("androidDidLogin: accessToken=[" + accessToken + "]")

    // The Android app can only send string values, convert "null" and "" to JavaScript null
    if (accessToken === undefined || accessToken === "null" || accessToken === "") {
      accessToken = null;
    }

    // Android responded with an access token, send it back to the receiver
    this.onResponse(accessToken)
  }


  // Windows

  loginWindows() {
    console.log("loginWindows")
    // Enable the Windows app to call back
    window.facebookLogin = this

    // Tell the Windows app to get an access token
    postMessageToWindows(new Message(Message.ACTION_DO_FACEBOOK_LOGIN, {}))
  }

  windowsDidLogin(accessToken) {
    console.log("windowsDidLogin: accessToken=[" + accessToken + "]")

    // The Windows app can only send string values, convert "null" and "" to JavaScript null
    if (accessToken === undefined || accessToken === "null" || accessToken === "") {
      accessToken = null;
    }

    // Send accessToken back to the receiver
    this.onResponse(accessToken)
  }
}


/**
 * Provides an easy way to logout of Facebook. This class uses different methods to logout of Facebook depending on the
 * current platform. Once logged out, the provided onLoggedOut callback is called.
 */
export class FacebookLogout {
  constructor(onLoggedOut) {
    this.onLoggedOut = onLoggedOut
  }

  logout() {
    if (isAndroid(window.queryParams.device)) {
      // Facebook doesn't allow login from Android WebView, so we have to ask the Android app to logout
      this.logoutAndroid()
    } else if (isWindows(window.queryParams.device)) {
      // Facebook login doesn't work in Windows embedded browser, so we have to ask the Windows app to logout
      this.logoutWindows()
    } else {
      // For every other platform, use Facebook JavaScript SDK
      this.logoutJSSDK()
    }
  }


  // JavaScript SDK

  logoutJSSDK() {
    window.FB.logout(response => {
      console.log("logoutJSSDK: Successfully logged out")
      this.onLoggedOut()
    })
  }


  // Android

  logoutAndroid() {
    console.log("logoutAndroid")
    // Enable the Android app to call back
    window.facebookLogout = this

    // Tell the Android app to logout
    postMessageToAndroid(new Message(Message.ACTION_DO_FACEBOOK_LOGOUT, {}))
  }

  androidDidLogout() {
    console.log("androidDidLogout")
    this.onLoggedOut()
  }


  // Windows

  logoutWindows() {
    console.log("logoutWindows")
    // // Enable the Windows app to call back
    // window.facebookLogout = this
    //
    // // Tell the Windows app to logout
    // postMessageToWindows(new Message(Message.ACTION_DO_FACEBOOK_LOGOUT, {}))

    this.onLoggedOut()
  }

  windowsDidLogout() {
    console.warn("windowsDidLogout not implemented")
    // this.onLoggedOut()
  }
}


/**
 * Checks with Facebook to see if the provided permissions have been granted for the provided access token.
 *
 * @param onResponse Callback which will receive a boolean indicating whether or not permissions are granted.
 * @param accessToken The Facebook access token.
 * @param permissions A String array of Facebook permissions.
 */
export function arePermissionsGranted(onResponse, accessToken, permissions) {
  // Check to see that we have the permissions we need
  let declined = []
  let granted = []
  // let requests = []
  window.FB.api('/me/permissions', response => {
    for (let i = 0; i < response.data.length; i++) {
      if (response.data[i].status == 'declined') {
        declined.push(response.data[i].permission)
      } else {
        granted.push(response.data[i].permission)
      }
    }
    console.info("facebook.js arePermissionsGranted: granted permissions: " + JSON.stringify(granted))
    console.warn("facebook.js arePermissionsGranted: declined permissions: " + JSON.stringify(declined))

    let arePermissionsGranted = true
    for (let i = 0; i < permissions.length; i++) {
      const permission = permissions[i]
      arePermissionsGranted = includes(granted, permission)
    }
    console.log("arePermissionsGranted: areGranted=[" + arePermissionsGranted + "]")
    onResponse(arePermissionsGranted)
  }, { access_token: accessToken })
}

/**
 * Array Utility function because built-in 'includes' doesn't exist in Internet Explorer which the Windows Desktop App
 * uses.
 */
function includes(array, value) {
  if (!array || array.length === 0) {
    return false;
  }
  for (let i = 0; i < array.length; i++) {
    let v = array[i]
    if (v === value) {
      return true
    }
  }
  return false
}


// WEBPACK FOOTER //
// ./src/Helpers/facebook.js